import { formatDuration, formatLog } from "./formatManager.js";

import { openDialog } from "../../app-dialog/utils/core/dialogManager.js";
import { toast } from "../../../layout/app-toast/utils/toastManager.js";
import { currentTable, currentView } from "../../../../app/viewManager.js";
import { addFinishedTimeLog } from "../../app-table/tableManager/timeLogManager.js";
import { apiFetch } from "../../../../app/apiClient.js";

const timeOptions = { hour: '2-digit', minute: '2-digit' };
const dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

function formatStartSub(rawStartTime) {
    if (!rawStartTime) return "Heute";
    const startDate = new Date(rawStartTime);
    if (isNaN(startDate)) return "Heute";
    return startDate.toDateString() === new Date().toDateString()
        ? "Heute"
        : startDate.toLocaleDateString('de-DE', dateOptions);
}

let state = {
    currentStartTimestamp: null,
    currentBreakStartTimestamp: null,
    accumulatedMs: 0,
    accumulatedBreakMs: 0,
    timerInterval: null,
    isFinished: false,
    dayClosedAt: null,
    openTaskId: null,
    openTaskIdInitialized: false,
};

function computeTimerState(logs, dayClosedAt) {
    const isFinished = !!dayClosedAt;

    if (!logs?.length) return {
        isRunning: false, isFinished,
        accumulatedMs: 0, accumulatedBreakMs: 0,
        startTimestamp: null, lastStopTimestamp: null,
        firstStart: "--:--", firstStartRaw: null, lastStop: "--:--"
    };

    const openEntry     = logs.find(l => !l.endTime) ?? null;
    const lastCompleted = logs.find(l => l.endTime);

    const accumulatedMs = logs
        .filter(l => l.endTime)
        .reduce((sum, l) => sum + Math.max(0, new Date(l.endTime) - new Date(l.startTime)), 0);

    const sortedLogs = [...logs].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    let accumulatedBreakMs = 0;
    for (let i = 0; i < sortedLogs.length - 1; i++) {
        const currentEnd = new Date(sortedLogs[i].endTime);
        const nextStart  = new Date(sortedLogs[i + 1].startTime);
        if (!isNaN(currentEnd) && !isNaN(nextStart) && nextStart > currentEnd)
            accumulatedBreakMs += (nextStart - currentEnd);
    }

    return {
        isRunning:         !!openEntry && !isFinished,
        isFinished,
        accumulatedMs,
        accumulatedBreakMs,
        startTimestamp:    openEntry     ? new Date(openEntry.startTime).getTime()      : null,
        lastStopTimestamp: !openEntry && lastCompleted ? new Date(lastCompleted.endTime).getTime() : null,
        firstStart:        new Date(sortedLogs[0].startTime).toLocaleTimeString('de-DE', timeOptions),
        firstStartRaw:     sortedLogs[0].startTime,
        lastStop:          lastCompleted ? new Date(lastCompleted.endTime).toLocaleTimeString('de-DE', timeOptions) : "--:--",
    };
}

function syncStateFromTable() {
    const logs = currentTable.getData().map(r => ({ id: r.id, startTime: r.startRaw, endTime: r.endRaw }));
    const serverState = computeTimerState(logs, state.isFinished ? true : null);

    state.accumulatedMs      = serverState.accumulatedMs;
    state.accumulatedBreakMs = serverState.accumulatedBreakMs;

    if (serverState.isRunning) {
        state.currentStartTimestamp      = serverState.startTimestamp;
        state.currentBreakStartTimestamp = null;
    } else if (!state.isFinished) {
        state.currentStartTimestamp      = null;
        state.currentBreakStartTimestamp = logs.length > 0 ? (serverState.lastStopTimestamp ?? Date.now()) : null;
    }

    const appTime = document.querySelector("app-time");
    if (appTime) {
        appTime.cardStart.setAttribute("value", serverState.firstStart ?? "--:--");
        appTime.cardStart.setAttribute("sub", formatStartSub(serverState.firstStartRaw));
        appTime.lastStopValue.textContent = serverState.lastStop ?? "--:--";
    }

    if (state.currentStartTimestamp || state.currentBreakStartTimestamp)
        startGlobalInterval();
    else
        updateTimerUI();

    updateButtonStates();
}

function updateTimerUI() {
    const appTime = document.querySelector("app-time");
    if (!appTime) return;

    if (state.currentStartTimestamp !== null) {
        const currentWorkMs = Date.now() - state.currentStartTimestamp;
        appTime.cardWork.setAttribute("value", formatDuration(state.accumulatedMs + currentWorkMs));
        appTime.cardWork.setAttribute("sub",   `Aktuell: ${formatDuration(currentWorkMs)}`);
        appTime.cardBreak.setAttribute("value", formatDuration(state.accumulatedBreakMs));
        appTime.cardBreak.setAttribute("sub",   "Aktuell: 00:00:00");
    }
    else if (state.currentBreakStartTimestamp !== null) {
        const currentBreakMs = Date.now() - state.currentBreakStartTimestamp;
        appTime.cardWork.setAttribute("value", formatDuration(state.accumulatedMs));
        appTime.cardWork.setAttribute("sub",   "Aktuell: 00:00:00");
        appTime.cardBreak.setAttribute("value", formatDuration(state.accumulatedBreakMs + currentBreakMs));
        appTime.cardBreak.setAttribute("sub",   `Aktuell: ${formatDuration(currentBreakMs)}`);
    }
    else {
        appTime.cardWork.setAttribute("value",  formatDuration(state.accumulatedMs));
        appTime.cardWork.setAttribute("sub",    "Aktuell: 00:00:00");
        appTime.cardBreak.setAttribute("value", formatDuration(state.accumulatedBreakMs));
        appTime.cardBreak.setAttribute("sub",   "Aktuell: 00:00:00");
    }
}

function startGlobalInterval() {
    if (state.timerInterval) clearInterval(state.timerInterval);
    updateTimerUI();
    state.timerInterval = setInterval(updateTimerUI, 1000);
}

export function getOpenTaskId() {
    return state.openTaskId;
}

export async function ensureOpenTaskId() {
    if (state.openTaskIdInitialized) return;
    const raw = await apiFetch("currentTimelogs/today");
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        state.openTaskId = raw.openTaskId ?? null;
    }
    state.openTaskIdInitialized = true;
}

export function getTrackedWorkMs() {
    const currentWorkMs = state.currentStartTimestamp !== null
        ? Date.now() - state.currentStartTimestamp
        : 0;
    return state.accumulatedMs + currentWorkMs;
}

export async function ensureTrackedWorkMs(fallbackMs = null) {
    const ms = getTrackedWorkMs();
    if (ms > 0) return ms;
    if (fallbackMs !== null) return fallbackMs;

    const raw = await apiFetch("currentTimelogs/today");
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 0;
    const logs = raw.logs ?? [];
    const accumulated = logs
        .filter(l => l.endTime)
        .reduce((sum, l) => sum + (new Date(l.endTime) - new Date(l.startTime)), 0);
    const openLog = logs.find(l => !l.endTime);
    const current = openLog ? Date.now() - new Date(openLog.startTime).getTime() : 0;
    return accumulated + current;
}

function applyTodayRaw(raw, table) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;

    const logs        = raw.logs ?? [];
    const dayClosedAt = raw.dayClosedAt ?? null;
    logs.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

    const rows        = logs.map(formatLog);
    const serverState = computeTimerState(logs, dayClosedAt);

    const tbl = table ?? currentTable;
    if (!tbl) return;

    tbl.options.placeholder = "Keine Zeiteinträge vorhanden. Klicke auf 'Start', um deine Zeit zu erfassen.";
    tbl.replaceData(rows);

    const appTime = document.querySelector("app-time");
    if (!appTime) return;

    state.accumulatedMs      = serverState.accumulatedMs || 0;
    state.accumulatedBreakMs = serverState.accumulatedBreakMs || 0;
    state.dayClosedAt        = dayClosedAt ? new Date(dayClosedAt).getTime() : null;
    state.openTaskId = raw.openTaskId ?? null;
    state.openTaskIdInitialized = true;
    updateTaskButton();

    appTime.cardStart.setAttribute("value", serverState.firstStart ?? "--:--");
    appTime.cardStart.setAttribute("sub",   formatStartSub(serverState.firstStartRaw));
    appTime.cardEnd.setAttribute("value",   serverState.isFinished ? serverState.lastStop : "--:--");
    appTime.cardEnd.setAttribute("sub",     serverState.isFinished ? "Feierabend" : "Noch aktiv");
    appTime.lastStopValue.textContent = serverState.lastStop ?? "--:--";

    if (serverState.isRunning) {
        appTime.status.textContent = "Läuft";
        state.currentStartTimestamp      = serverState.startTimestamp;
        state.currentBreakStartTimestamp = null;
        state.isFinished = false;
    }
    else if (serverState.isFinished) {
        appTime.status.textContent = "Feierabend";
        state.currentStartTimestamp      = null;
        state.currentBreakStartTimestamp = null;
        state.isFinished = true;
    }
    else if (rows.length > 0) {
        appTime.status.textContent = "Pause";
        state.currentStartTimestamp      = null;
        state.currentBreakStartTimestamp = serverState.lastStopTimestamp ?? Date.now();
        state.isFinished = false;
    }
    else {
        appTime.status.textContent = "Inaktiv";
        state.currentStartTimestamp      = null;
        state.currentBreakStartTimestamp = null;
        state.isFinished = false;
    }

    if (state.currentStartTimestamp || state.currentBreakStartTimestamp)
        startGlobalInterval();
    else
        updateTimerUI();

    updateButtonStates();
}

export async function initTime(table) {
    const raw = await apiFetch("currentTimelogs/today");
    applyTodayRaw(raw, table);
}

function showRestartDialog() {
    return new Promise(resolve => {
        let resolved = false;
        const safeResolve = (val) => { if (!resolved) { resolved = true; resolve(val); } };

        openDialog("restartDay", { resolve: safeResolve });

        const dlg = document.querySelector("app-dialog");
        if (dlg) dlg.addEventListener("close", () => safeResolve(null), { once: true });
    });
}

async function doStart(mode) {
    const url = mode === 'continue'
        ? "currentTimelogs/start?continueLastDay=true"
        : "currentTimelogs/start";

    const log = await apiFetch(url);
    if (log === 409) {
        await initTime();
        return;
    }
    if (!log || typeof log !== 'object') {
        toast("Fehler beim Starten der Zeit.", "error");
        return;
    }

    // Re-sync from the server instead of patching state locally: starting a
    // new timer can land on either the same workday or a brand-new one
    // (e.g. after finishing a day that ran past midnight), and only the
    // server knows which. This mirrors what a page reload already does.
    await initTime();
    toast("Zeit fortgesetzt", "success");
}

export async function startTime() {
    if (state.currentStartTimestamp !== null) {
        toast("Deine Zeit wird bereits erfasst!", "error");
        return;
    }

    let mode = 'normal';

    if (state.isFinished) {
        const now            = new Date();
        const hourNow        = now.getHours();
        const closedOnSameDay = state.dayClosedAt !== null
            && new Date(state.dayClosedAt).toDateString() === now.toDateString();

        if (closedOnSameDay) {
            mode = 'normal';
        } else if (hourNow >= 5) {
            mode = 'newDay';
        } else {
            const choice = await showRestartDialog();
            if (choice === null) return;
            mode = choice;
        }

        state.isFinished  = false;
        state.dayClosedAt = null;
    }

    await doStart(mode);
}

export async function stopTime(feedback = true) {
    if (state.isFinished) {
        toast("Der Arbeitstag ist bereits beendet!", "error");
        return;
    }

    if (state.currentStartTimestamp === null) {
        toast("Du bist bereits in der Pause!", "error");
        return;
    }

    const log = await apiFetch("currentTimelogs/stop");
    if (!log || typeof log !== 'object') {
        toast("Fehler beim Stoppen der Zeit.", "error");
        return;
    }

    state.accumulatedMs += (Date.now() - state.currentStartTimestamp);

    const row = formatLog(log);
    state.currentStartTimestamp      = null;
    state.currentBreakStartTimestamp = Date.now();

    const appTime = document.querySelector("app-time");
    if (appTime) appTime.status.textContent = "Pause";

    currentTable.updateRow(row.id, row);

    startGlobalInterval();
    updateButtonStates();
    if (feedback) toast("Pause gestartet", "success");
}

function getLastTableEntry() {
    return currentTable.getData().reduce((latest, row) =>
        !latest || new Date(row.startRaw) > new Date(latest.startRaw) ? row : latest
    , null);
}

export async function updateTime(Id) {
    const content = document.querySelector("app-dialog")?.contentEl;
    if (!content) return;

    const startInput = content.querySelector("#dropdownStart")?.getValue?.() ?? "";
    const endInput   = content.querySelector("#dropdownEnd")?.getValue?.()   ?? "";

    if (!startInput) {
        toast("Startzeit ist erforderlich!", "error");
        return true;
    }

    const isLastEntry = getLastTableEntry()?.id === Id;
    if (!endInput && !isLastEntry) {
        toast("Endzeit ist erforderlich – nur beim letzten Eintrag kann sie entfernt werden.", "error");
        return true;
    }

    const rowData = currentTable.getRow(Id)?.getData();
    if (!rowData) return;

    const datePart  = rowData.startRaw.substring(0, 10);
    const startTime = `${datePart}T${startInput}:00`;

    if (endInput && endInput <= startInput) {
        toast("Endzeit muss nach der Startzeit liegen!", "error");
        return true;
    }

    const body = { startTime };
    if (endInput) {
        body.endTime = `${datePart}T${endInput}:00`;
    }

    const log = await apiFetch(`currentTimelogs/${Id}`, "PATCH", body);
    if (!log || typeof log !== 'object') return;

    currentTable.updateData([formatLog(log)]);
    syncStateFromTable();
    toast("Zeiteintrag erfolgreich aktualisiert!", "success");
    return log;
}

export async function deleteTime(id) {
    const result = await apiFetch(`currentTimelogs/${id}`, "DELETE");
    if (result == null) return;

    currentTable.deleteRow(id);
    syncStateFromTable();
    toast("Zeiteintrag erfolgreich gelöscht!", "success");
}

export async function finishTime() {
    if (state.isFinished) {
        toast("Der Arbeitstag ist bereits beendet!", "error");
        return;
    }

    if (state.currentStartTimestamp === null && state.currentBreakStartTimestamp === null) {
        toast("Es läuft keine aktive Sitzung!", "error");
        return;
    }

    const result = await apiFetch("currentTimeLogs/finish");
    if (!result) return;

    state.currentStartTimestamp      = null;
    state.currentBreakStartTimestamp = null;
    state.isFinished  = true;
    state.dayClosedAt = Date.now();

    if (state.timerInterval) {
        clearInterval(state.timerInterval);
        state.timerInterval = null;
    }

    if (state.openTaskId) {
        const prevId = state.openTaskId;
        state.openTaskId = null;
        if (currentView === 'Entries' && currentTable) {
            currentTable.updateData([{ id: prevId, isRunning: false }]);
        }
    }

    const appTime = document.querySelector("app-time");
    if (appTime) {
        const endVal = result.row?.end ?? new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
        appTime.cardEnd.setAttribute("value", endVal);
        appTime.cardEnd.setAttribute("sub",   "Feierabend");
        appTime.status.textContent = "Feierabend";
        updateTimerUI();
    }

    if (currentView === 'Time' && currentTable) {
        applyTodayRaw(result, currentTable);
    } else if (currentView === 'Timelogs' && currentTable) {
        await addFinishedTimeLog(currentTable);
    }

    updateTaskButton();
    toast("Schönen Feierabend!", "success");
}

function updateTaskButton() {
    const appTime = document.querySelector("app-time");
    if (!appTime?.startTaskButton) return;
    const isOpen = state.openTaskId !== null;
    appTime.startTaskButton.innerHTML = isOpen
        ? '<i class="fa-solid fa-stopwatch"></i> Aufgabe beenden'
        : '<i class="fa-solid fa-stopwatch"></i> Aufgabe beginnen';
    appTime.startTaskButton.classList.toggle("primary", !isOpen);
    appTime.startTaskButton.classList.toggle("warning", isOpen);
    updateButtonStates();
}

function updateButtonStates() {
    const appTime = document.querySelector("app-time");
    if (!appTime) return;

    const running  = state.currentStartTimestamp !== null;
    const paused   = !running && state.currentBreakStartTimestamp !== null;
    const finished = state.isFinished;
    const hasTask  = state.openTaskId !== null;

    if (appTime.startButton) {
        appTime.startButton.disabled = running;
        appTime.startButton.classList.toggle("attention", !running);
    }
    if (appTime.stopButton)      appTime.stopButton.disabled      = !running;
    if (appTime.finishButton)    appTime.finishButton.disabled    = finished || (!running && !paused);
    if (appTime.startTaskButton) appTime.startTaskButton.disabled = finished || (!running && !hasTask);
}

export function setOpenTaskId(id) {
    state.openTaskIdInitialized = true;
    const prevId = state.openTaskId;
    state.openTaskId = id ?? null;
    updateTaskButton();

    if (currentView === 'Entries' && currentTable) {
        if (prevId) currentTable.updateData([{ id: prevId, isRunning: false }]);
        if (id)     currentTable.updateData([{ id,      isRunning: true  }]);
    }
}

export async function startTask() {
    if (state.openTaskId !== null) {
        await finishTask();
    } else {
        openDialog("createEntry", null, true);
    }
}

export async function finishTask() {
    const taskId = state.openTaskId;
    const result = await apiFetch(`Entries/${taskId}/Finish`, "PATCH", undefined, { silentBadRequest: true });

    if (typeof result === 'number') {
        state.openTaskId = null;
        updateTaskButton();
        if (currentView === 'Entries' && currentTable && taskId) {
            currentTable.deleteRow(taskId);
        }
        if (result === 400) {
            toast("Aufgabe war kürzer als 15 Minuten und wurde verworfen.", "info");
        }
        return;
    }

    if (!result) return;

    state.openTaskId = null;
    updateTaskButton();
    toast("Aufgabe beendet", "success");

    if (currentView === 'Entries' && currentTable && taskId) {
        const patch = { id: taskId, isRunning: false };
        if (typeof result === 'object') {
            patch.workedHours = result.workedHours ?? null;
            patch.date = result.date ?? null;
        }
        currentTable.updateData([patch]);
    }
}